import authService from '@/services/AuthService';
import { LogoutOutlined, SettingOutlined, UserOutlined } from '@ant-design/icons';
import { history, useModel } from '@umijs/max';
import { Spin, message } from 'antd';
import type { MenuProps } from 'antd';
import { createStyles } from 'antd-style';
import { stringify } from 'querystring';
import React, { useState } from 'react';
import { flushSync } from 'react-dom';
import HeaderDropdown from '../HeaderDropdown';
import UserConfig from '../UserConfig/UserConfig';
import ChangePassword from '@/components/UserProfile/ChangePassword';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};
  
  // On mobile hide the user name (only show on desktop)
  const isMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : false;
  if (isMobile) return null;

  // Get user name from AuthService or fallback to initialState
  const session = authService.getCurrentSession();
  const userName = session?.user || currentUser?.name || 'User';
  
  return <span className="anticon">{userName}</span>;
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({ menu, children }) => {
  const [configVisible, setConfigVisible] = useState(false);
  const [profileVisible, setProfileVisible] = useState(false);
  
  const loginOut = async () => {
    try {
      // Show loading message
      const hide = message.loading('Cerrando sesión...', 0);
      
      // Check if user is authenticated before attempting logout
      const isAuthenticated = await authService.isAuthenticated();
      
      if (!isAuthenticated) {
        hide();
        // If not authenticated, just redirect to login
        const { search, pathname } = window.location;
        const urlParams = new URL(window.location.href).searchParams;
        const redirect = urlParams.get('redirect');
        if (window.location.pathname !== '/login' && !redirect) {
          history.replace({
            pathname: '/login',
            search: stringify({
              redirect: pathname + search,
            }),
          });
        }
        return;
      }
      
      // Perform logout using AuthService
      await authService.logout();
      hide();
      
      // Show success message
      message.success('Sesión cerrada exitosamente');
      
      // Redirect to login page
      const { search, pathname } = window.location;
      const urlParams = new URL(window.location.href).searchParams;
      const redirect = urlParams.get('redirect');
      if (window.location.pathname !== '/login' && !redirect) {
        history.replace({
          pathname: '/login',
          search: stringify({
            redirect: pathname + search,
          }),
        });
      }
    } catch (error) {
      message.error('Error al cerrar sesión');
      // Even if logout fails, redirect to login page
      history.replace({ pathname: '/login' });
    }
  };
  const { styles } = useStyles();

  const { initialState, setInitialState } = useModel('@@initialState');

  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'logout') {
      flushSync(() => {
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      loginOut();
      return;
    }
    if (key === 'settings') {
      setConfigVisible(true);
      return;
    }
    if (key === 'mi-perfil') {
      setProfileVisible(true);
      return;
    }
    history.push(`/account/${key}`);
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );
  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;
  
  // Check if user is authenticated using AuthService
  const session = authService.getCurrentSession();
  const isAuthenticated = session?.isAuthenticated || false;
  
  if (!isAuthenticated && (!currentUser || !currentUser.name)) {
    return loading;
  }

  const menuItems = [
    ...(menu
      ? [
          {
            key: 'center',
            label: 'Centro de Usuario',
            icon: <UserOutlined />,
          },
          {
            key: 'settings',
            label: 'Configuración',
            icon: <SettingOutlined />,
          },
          {
            type: 'divider' as const,
          },
        ]
      : []),
    {
      key: 'logout',
      label: 'Cerrar sesión',
      icon: <LogoutOutlined />,
    },
    {
      key: 'mi-perfil',
      label: 'Mi perfil',
      icon: <UserOutlined />,
    },
  ];

  // Get current user info for UserConfig
  const currentUserConfig = session ? {
    id: session.fk_user,
    name: session.user,
    email: session.email,
    rol: session.rol || 'Usuario'
  } : undefined;

  return (
    <>
      <HeaderDropdown
        menu={{
          selectedKeys: [],
          onClick: onMenuClick,
          items: menuItems,
        }}
      >
        {}
        {typeof window !== 'undefined' && window.innerWidth < 768 ? null : children}
      </HeaderDropdown>
      
      <UserConfig
        visible={configVisible}
        onClose={() => setConfigVisible(false)}
        currentUser={currentUserConfig}
      />
      <ChangePassword
        visible={profileVisible}
        onClose={() => setProfileVisible(false)}
      />
    </>
  );
};
